import { currentViewConfigAtom } from "datasheets/model/atoms/WorkbookFocusAtoms";
import { Atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ViewConfig } from "datasheets/model/core/Workbook";
import { Card, CardContent, Stack, Tooltip } from "@mui/material";
import { FormViewConfig } from "./FormViewConfig";
import { FormComponentFactory } from "./FormComponentFactory";
import { Component } from "./Component";
import { PropsWithChildren, useCallback, useMemo } from "react";
import { DropTargetMonitor, useDrag, useDrop } from "react-dnd";
import { Add, Cancel, Delete } from "@mui/icons-material";
import { filterDict } from "datasheets/model/utils/dictionaryUtils";
import { newWire } from "./state/ComponentAtoms";
import {
  componentInputEndpointsAtom,
  componentInputConnectorsAtom,
  createWireAtom,
  deleteWireAtom,
  componentOutputEndpointsAtom,
  componentOutputConnectorsAtom,
} from "./wireAtoms";
import { selectedComponentAtom } from "./selectedComponentAtom";
import { stringToColour } from "./stringToColour";

export function EditableComponent({
  config,
  children,
}: PropsWithChildren<{ config: Component }>) {
  const setView = useSetAtom(currentViewConfigAtom);
  const setSelectedComponent = useSetAtom(selectedComponentAtom);
  const componentId = config.id;
  const [{ isDragging }, drag] = useDrag({
    type: "COMPONENT_DRAG",
    item: { id: config.id },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const onDelete = useCallback(() => {
    // If component was selected, unselect
    setSelectedComponent((id) => (id === componentId ? null : id));
    // And then remove from components
    setView((currentView: ViewConfig) => {
      const view = currentView as FormViewConfig;
      const components = filterDict(
        view.components,
        (id) => id !== componentId
      );
      return { ...view, ...{ components: components } };
    });
  }, [componentId, setView, setSelectedComponent]);
  const invisibleComponent = <div ref={drag} />;
  const visibleComponent = (
    <div
      ref={drag /* Draggable container */}
      style={{
        top: config.top + "px",
        left: config.left + "px",
        position: "absolute",
        cursor: "move",
        width: "fit-content",
      }}
    >
      <Stack direction="row">
        <div id={"Left Controls"}>
          <InputConnectors
            componentId={componentId}
            componentType={config.type}
          />
        </div>
        <div
          style={{
            borderColor: "blue",
            borderWidth: "5px",
            borderStyle: "inset",
            borderRadius: "12px",
            padding: "5px",
          }}
        >
          <Card
            sx={{ minHeight: "40vh", minWidth: "10vw" }}
            onClick={() => setSelectedComponent(config.id)}
          >
            <CardContent>{children}</CardContent>
          </Card>
        </div>
        <div
          id="rightControls"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
          }}
        >
          <div>
            <Cancel sx={{ color: "red" }} onClick={onDelete} />
          </div>
          <OutputConnectors
            componentId={componentId}
            componentType={config.type}
          />
        </div>
      </Stack>
    </div>
  );
  if (isDragging) {
    return invisibleComponent;
  }
  return visibleComponent;
}
function InputConnectors({
  componentId,
  componentType,
}: {
  componentId: string;
  componentType: string;
}) {
  const inputsAtom = useMemo(
    () => componentInputEndpointsAtom(componentId),
    [componentId]
  );
  const setViewConfig = useSetAtom(currentViewConfigAtom);
  const [customInputNames, setCustomInputNames] = useAtom(inputsAtom);
  const fixedInputNames = FormComponentFactory.getFixedInputs(componentType);
  const inputNames = [...fixedInputNames, ...customInputNames];
  const inputConnectors = useAtomValue(
    useMemo(() => componentInputConnectorsAtom(componentId), [componentId])
  );
  const createWire = useSetAtom(createWireAtom);
  const deleteWire = useSetAtom(deleteWireAtom);
  const [{ isOver }, drop] = useDrop({
    accept: "INPUT_COMPONENT",
    drop({ id }: { id: { componentId: string; name: string } }, monitor): void {
      removeInput(id);
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
    }),
  });
  const addWire = (
    src: { componentId: string; name: string },
    dest: string
  ) => {
    setViewConfig((config: FormViewConfig) => {
      const srcConfig = { ...config.components[src.componentId] };
      const targetConfig = { ...config.components[componentId] };
      const wireId = newWire();
      createWire({
        wireId: wireId,
        src: src,
        dest: { componentId: componentId, name: dest },
      });
      if (!("connections" in srcConfig.editConfig)) {
        Object.defineProperty(srcConfig.editConfig, "connections", {
          value: {},
          enumerable: true,
        });
      }
      if (!("outputConnectors" in srcConfig.editConfig.connections)) {
        Object.defineProperty(
          srcConfig.editConfig.connections,
          "outputConnectors",
          { value: {}, enumerable: true }
        );
      }
      srcConfig.editConfig.connections.outputConnectors[src.name] = wireId;
      if (!("connections" in targetConfig.editConfig)) {
        Object.defineProperty(targetConfig.editConfig, "connections", {
          value: {},
          enumerable: true,
        });
      }
      if (!("inputConnectors" in targetConfig.editConfig.connections)) {
        Object.defineProperty(
          targetConfig.editConfig.connections,
          "inputConnectors",
          { value: {}, enumerable: true }
        );
      }
      targetConfig.editConfig.connections.inputConnectors[dest] = wireId;
      return {
        ...config,
        ...{
          components: {
            ...config.components,
            ...{ [src.componentId]: srcConfig, [componentId]: targetConfig },
          },
        },
      };
    });
  };
  const inputs = inputNames.map((name) => {
    return (
      <InputTab
        key={name}
        componentId={componentId}
        name={name}
        addWire={addWire}
        removeWire={deleteWire}
        wire={inputConnectors[name]}
      />
    );
  });
  const removeInput = (inputId: { componentId: string; name: string }) => {
    if (inputId.componentId === componentId) {
      const wireId = inputConnectors[inputId.name];
      // First remove any associated wires
      if (wireId) {
        deleteWire(wireId);
      }
      // Remove input from component
      setCustomInputNames((inputNames: string[]) => {
        const newNames = inputNames.filter((name) => name !== inputId.name);
        return newNames;
      });
    }
  };
  const addInput = useCallback(() => {
    setCustomInputNames((inputNames: string[]) => {
      let nextInputNum = inputNames.length - 1;
      let nextInputName;
      do {
        nextInputNum += 1;
        nextInputName = "Input" + nextInputNum;
      } while (inputNames.includes(nextInputName));
      const newNames = [...inputNames];
      newNames.push(nextInputName);
      return newNames;
    });
  }, [setCustomInputNames]);
  const contentWidth = 20;
  return (
    <div
      style={{
        position: "absolute",
        top: "0px",
        left: 5 - contentWidth + "px",
        width: contentWidth + "px",
        display: "flex",
        justifyContent: "right",
        alignContent: "normal",
      }}
    >
      <Stack direction="column">
        <Tooltip title="add input">
          <div ref={drop}>
            {isOver ? (
              <Delete style={{ position: "relative", left: "-5px" }} />
            ) : (
              <Add
                onClick={addInput}
                style={{ position: "relative", left: "-5px" }}
              />
            )}
          </div>
        </Tooltip>
        {inputs}
      </Stack>
    </div>
  );
}
function OutputConnectors({
  componentId,
  componentType,
}: {
  componentId: string;
  componentType: string;
}) {
  const customOutputsAtom: Atom<string[]> = useMemo(
    () => componentOutputEndpointsAtom(componentId),
    [componentId]
  );
  const customOutputNames = useAtomValue(customOutputsAtom);
  const outputConnectors = useAtomValue(
    useMemo(() => componentOutputConnectorsAtom(componentId), [componentId])
  );
  const fixedOutputNames = FormComponentFactory.getFixedOutputs(componentType);
  const outputNames = [...fixedOutputNames, ...customOutputNames];
  const outputs = outputNames.map((name) => {
    return (
      <OutputTab
        key={name}
        name={name}
        componentId={componentId}
        wire={outputConnectors[name]}
      />
    );
  });
  return (
    <div
      style={{
        marginBottom: "10px",
      }}
    >
      {outputs}
    </div>
  );
}
function InputTab({
  componentId,
  name,
  addWire,
  removeWire,
  wire,
}: {
  componentId: string;
  name: string;
  addWire: (src: { componentId: string; name: string }, dest: string) => void;
  removeWire: (wireId: string) => void;
  wire: string | undefined;
}) {
  const [, drag] = useDrag({
    type: "INPUT_COMPONENT",
    item: { id: { componentId, name, wire } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "COMPONENT_CONNECTOR",
    drop({ id }: { id: { componentId: string; name: string } }, monitor): void {
      if (wire) {
        // Should confirm with user before dropping
        removeWire(wire);
      }
      addWire(id, name);
    },
    collect: (monitor: DropTargetMonitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
  });
  return (
    <div
      ref={(el) => {
        drag(el);
        drop(el);
      }}
    >
      <InputOutputTab
        name={name}
        color={isOver && canDrop ? "red" : stringToColour(wire)}
      />
    </div>
  );
}
function OutputTab({
  componentId,
  name,
  wire,
}: {
  componentId: string;
  name: string;
  wire: string | undefined;
}) {
  const deleteWire = useSetAtom(deleteWireAtom);
  const [, drag] = useDrag({
    type: "COMPONENT_CONNECTOR",
    item: { id: { componentId, name } },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: "INPUT_COMPONENT",
    canDrop(
      { id }: { id: { componentId: string; name: string; wire: string } },
      monitor
    ): boolean {
      return !!id.wire && id.wire === wire;
    },
    drop(
      { id }: { id: { componentId: string; name: string; wire: string } },
      monitor
    ): void {
      console.log("Drop", id);
      if (!!id.wire && id.wire === wire) {
        deleteWire(wire);
      }
    },
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });
  return (
    <div
      ref={(ref) => {
        drag(ref);
        drop(ref);
      }}
    >
      <InputOutputTab
        name={name}
        color={isOver && canDrop ? "red" : stringToColour(wire)}
      />
    </div>
  );
}
function InputOutputTab({ name, color }: { name: string; color?: string }) {
  return (
    <Tooltip title={name}>
      <div
        style={{
          position: "relative",
          width: "20px",
          height: "20px",
          background: color ?? "#0000ffaa",
          marginBottom: "5px",
        }}
      ></div>
    </Tooltip>
  );
}
